import React, { useEffect, useMemo, useState } from 'react';
import ConfirmModal from './ConfirmModal';
import {
  COUSIN_KIND_OPTIONS,
  FAMILY_SIDE_OPTIONS,
  RELATIONSHIP_CATEGORIES,
  categoryIcon,
  formatRelationshipSummary,
  relationIcon,
  relationOption,
  splitRelationsForCategory,
} from '../../utils/memberRelationshipCatalog';

const IconChoice = ({ icon, label, selected, onClick, title }) => (
  <button
    type="button"
    title={title || label}
    onClick={onClick}
    className={`flex-shrink-0 w-[4.35rem] flex flex-col items-center gap-1 rounded-xl px-1 py-1.5 transition-colors ${
      selected
        ? 'bg-primary-50 ring-2 ring-primary-500 dark:bg-primary-900/40'
        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
    }`}
  >
    <span className={`flex h-11 w-11 items-center justify-center rounded-full text-xl ${
      selected
        ? 'bg-white dark:bg-gray-900 shadow-sm'
        : 'bg-gray-100 dark:bg-gray-800'
    }`}>
      {icon}
    </span>
    <span className={`text-[10px] leading-tight text-center font-medium ${
      selected ? 'text-primary-800 dark:text-primary-100' : 'text-gray-600 dark:text-gray-300'
    }`}>
      {label}
    </span>
  </button>
);

const emptyDraft = () => ({
  categoryKey: '',
  explicitRelationKey: '',
  customDetail: '',
  familySide: '',
  cousinKind: '',
  anniversaryDate: '',
});

const draftFromSaved = (rel) => ({
  categoryKey: rel?.categoryKey || '',
  explicitRelationKey: rel?.explicitRelationKey || '',
  customDetail: rel?.customDetail || '',
  familySide: rel?.familySide || '',
  cousinKind: rel?.cousinKind || '',
  anniversaryDate: rel?.anniversaryDate || '',
});

const MemberRelationshipEditor = ({
  memberName,
  relationship,
  loading,
  saving,
  onSave,
  onDelete,
  startInAdd = false,
  onCancel,
}) => {
  const [step, setStep] = useState('view');
  const [draft, setDraft] = useState(emptyDraft());
  const [error, setError] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const firstName = String(memberName || 'this user').trim().split(/\s+/)[0] || 'this user';
  const selectedCategory = RELATIONSHIP_CATEGORIES.find((item) => item.key === draft.categoryKey) || null;
  const { icons: relationIcons, dropdown: relationDropdown } = useMemo(
    () => splitRelationsForCategory(draft.categoryKey),
    [draft.categoryKey],
  );
  const selectedRelation = relationOption(draft.categoryKey, draft.explicitRelationKey);
  const overflowSelected = relationDropdown.some((item) => item.key === draft.explicitRelationKey);

  const chooseRelation = (key) => {
    setDraft((prev) => ({
      ...prev,
      explicitRelationKey: key,
      customDetail: '',
      familySide: '',
      cousinKind: '',
      anniversaryDate: '',
    }));
    setError('');
  };
  const extras = selectedRelation?.extras || [];

  useEffect(() => {
    setError('');
    setShowRemoveConfirm(false);
    setDraft(relationship ? draftFromSaved(relationship) : emptyDraft());
    setStep(startInAdd && !relationship ? 'category' : 'view');
  }, [relationship, memberName, startInAdd]);

  const openAdd = () => {
    setError('');
    setDraft(relationship ? draftFromSaved(relationship) : emptyDraft());
    setStep(relationship ? 'relation' : 'category');
  };

  const chooseCategory = (category) => {
    setDraft({
      ...emptyDraft(),
      categoryKey: category.key,
    });
    setError('');
    setStep('relation');
  };

  const handleSave = async () => {
    if (!draft.categoryKey) {
      setError('Choose a relationship type');
      setStep('category');
      return;
    }
    if (!draft.explicitRelationKey) {
      setError('Choose how they are related to you');
      return;
    }
    if (extras.includes('anniversary') && !draft.anniversaryDate) {
      setError('Add the anniversary date');
      return;
    }
    if (extras.includes('familySide') && !draft.familySide) {
      setError('Choose paternal or maternal side');
      return;
    }
    if (extras.includes('cousinKind') && !draft.cousinKind) {
      setError('Choose which cousin this is');
      return;
    }
    if (extras.includes('customDetail') && (
      selectedRelation?.key?.includes('other') || selectedRelation?.key === 'known' || selectedRelation?.key === 'other_family'
    ) && !String(draft.customDetail || '').trim()) {
      setError('Please describe this relationship');
      return;
    }
    setError('');
    try {
      await onSave({
        categoryKey: draft.categoryKey,
        explicitRelationKey: draft.explicitRelationKey,
        customDetail: draft.customDetail.trim() || undefined,
        familySide: draft.familySide || undefined,
        cousinKind: draft.cousinKind || undefined,
        anniversaryDate: draft.anniversaryDate || undefined,
      });
      setStep('view');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save relationship');
    }
  };

  const handleDelete = async () => {
    if (!relationship) return;
    try {
      await onDelete();
      setDraft(emptyDraft());
      setStep('view');
      setShowRemoveConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not remove relationship');
    }
  };

  const withConfirm = (content) => (
    <>
      {content}
      <ConfirmModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={handleDelete}
        title="Remove relationship"
        message={`Remove your relationship with ${firstName}? This only changes how you tagged them.`}
        confirmText={saving ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        type="danger"
      />
    </>
  );

  if (loading) {
    return withConfirm(<p className="text-sm text-gray-500 dark:text-gray-400">Loading relationship…</p>);
  }

  if (step === 'category') {
    return withConfirm(
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Add relationship with {firstName}
          </h4>
          <button
            type="button"
            onClick={() => (onCancel ? onCancel() : setStep('view'))}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Pick a type. Extra details stay in a dropdown only when needed.
        </p>
        <div className="flex flex-wrap gap-1 max-h-72 overflow-y-auto">
          {RELATIONSHIP_CATEGORIES.map((category) => (
            <IconChoice
              key={category.key}
              icon={categoryIcon(category.key)}
              label={category.label}
              title={category.eventKinds}
              selected={draft.categoryKey === category.key}
              onClick={() => chooseCategory(category)}
            />
          ))}
        </div>
      </div>
    );
  }

  if (step === 'relation') {
    return withConfirm(
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <button
            type="button"
            onClick={() => setStep('category')}
            className="text-xs font-semibold text-primary-600 hover:text-primary-700"
          >
            ← Relationship type
          </button>
          <button
            type="button"
            onClick={() => (onCancel ? onCancel() : setStep('view'))}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
        {selectedCategory && (
          <div className="mb-3 rounded-xl bg-primary-50 dark:bg-primary-900/30 px-3 py-2">
            <p className="text-sm font-semibold text-primary-800 dark:text-primary-100">{selectedCategory.label}</p>
            <p className="mt-0.5 text-[11px] leading-snug text-primary-700/80 dark:text-primary-200/80">
              {selectedCategory.eventKinds}
            </p>
          </div>
        )}
        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
          How is {firstName} related to you?
        </label>
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-0.5 px-0.5">
          {relationIcons.map((item) => (
            <IconChoice
              key={item.key}
              icon={relationIcon(item.key)}
              label={item.label}
              selected={draft.explicitRelationKey === item.key}
              onClick={() => chooseRelation(item.key)}
            />
          ))}
        </div>
        {relationDropdown.length > 0 && (
          <select
            value={overflowSelected ? draft.explicitRelationKey : ''}
            onChange={(event) => chooseRelation(event.target.value)}
            className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
          >
            <option value="">More family…</option>
            {relationDropdown.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        )}

        {extras.includes('anniversary') && (
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Anniversary date
            </label>
            <input
              type="date"
              value={draft.anniversaryDate}
              onChange={(event) => setDraft((prev) => ({ ...prev, anniversaryDate: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
          </div>
        )}

        {extras.includes('familySide') && (
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Which side of the family?
            </label>
            <div className="flex gap-2">
              {FAMILY_SIDE_OPTIONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, familySide: item.key }))}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${
                    draft.familySide === item.key
                      ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-100'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {extras.includes('cousinKind') && (
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Which cousin?
            </label>
            <select
              value={draft.cousinKind}
              onChange={(event) => setDraft((prev) => ({ ...prev, cousinKind: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Select</option>
              {COUSIN_KIND_OPTIONS.map((item) => (
                <option key={item.key} value={item.key}>{item.label}</option>
              ))}
            </select>
          </div>
        )}

        {extras.includes('customDetail') && (
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
              {selectedRelation?.key === 'cousin' ? 'More detail (optional)' : 'Please specify'}
            </label>
            <input
              type="text"
              maxLength={160}
              value={draft.customDetail}
              onChange={(event) => setDraft((prev) => ({ ...prev, customDetail: event.target.value }))}
              placeholder={selectedRelation?.key === 'cousin' ? "e.g. mother's sister's son" : 'Add a short note'}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save relationship'}
        </button>
      </div>
    );
  }

  return withConfirm(
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
        Relationship
      </h4>
      {relationship ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {relationship.explicitRelationLabel} · {relationship.categoryLabel}
          </p>
          <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            {relationship.eventKinds}
          </p>
          <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">
            {formatRelationshipSummary(relationship)}
            {relationship.anniversaryDate ? ` · Anniversary ${relationship.anniversaryDate}` : ''}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Change
            </button>
            <button
              type="button"
              onClick={() => setShowRemoveConfirm(true)}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openAdd}
          className="w-full rounded-xl border border-dashed border-primary-300 dark:border-primary-700 px-3 py-3 text-left hover:bg-primary-50/70 dark:hover:bg-primary-900/20"
        >
          <p className="text-sm font-semibold text-primary-700 dark:text-primary-200">
            Add relationship with {firstName}
          </p>
          <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            Family, friend, colleague, and how you are related
          </p>
        </button>
      )}
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default MemberRelationshipEditor;
